import React from 'react'
import { motion } from 'framer-motion'
import { AlertCircle, CheckCircle, ArrowRight } from 'lucide-react'

const ProblemSolution: React.FC = () => {
  const problems = [
    "Ideas vanish before you can capture them",
    "Scattered notes across multiple apps",
    "No clear path from thought to published content",
    "Difficulty organizing and developing ideas",
    "Lost inspiration due to friction in recording"
  ]

  const solutions = [
    "Instant capture with Apple Watch voice recording",
    "Unified platform across all your devices",
    "Seamless workflow from idea to GitHub publication",
    "Visual status tracking: Seed → Sprout → Growing → Mature",
    "Zero-friction recording and intelligent organization"
  ]

  return (
    <section className="section-padding bg-gray-50">
      <div className="container-custom">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            The <span className="gradient-text">Creative Struggle</span> is Real
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Every creator faces the same challenge: brilliant ideas that slip away before they can be captured and developed into meaningful content.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Problem Side */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="bg-white rounded-2xl p-8 shadow-lg border border-red-100"
          >
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mr-4">
                <AlertCircle className="text-red-600" size={24} />
              </div>
              <h3 className="text-2xl font-bold text-gray-900">The Problem</h3>
            </div>
            
            <div className="space-y-4">
              {problems.map((problem, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, delay: 0.3 + index * 0.1 }}
                  viewport={{ once: true }}
                  className="flex items-start space-x-3"
                >
                  <div className="w-2 h-2 bg-red-400 rounded-full mt-3 flex-shrink-0"></div>
                  <p className="text-gray-700">{problem}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Solution Side */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            viewport={{ once: true }}
            className="bg-white rounded-2xl p-8 shadow-lg border border-primary-100"
          >
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mr-4">
                <CheckCircle className="text-primary-600" size={24} />
              </div>
              <h3 className="text-2xl font-bold text-gray-900">Our Solution</h3>
            </div>
            
            <div className="space-y-4">
              {solutions.map((solution, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, delay: 0.5 + index * 0.1 }}
                  viewport={{ once: true }}
                  className="flex items-start space-x-3"
                >
                  <div className="w-2 h-2 bg-primary-500 rounded-full mt-3 flex-shrink-0"></div>
                  <p className="text-gray-700">{solution}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Transformation Arrow */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          viewport={{ once: true }}
          className="flex justify-center mt-12"
        >
          <div className="bg-gradient-to-r from-primary-500 to-primary-600 text-white px-8 py-4 rounded-full flex items-center space-x-3 shadow-lg">
            <span className="font-semibold">Traditional Notes</span>
            <ArrowRight size={20} />
            <span className="font-semibold">Idea Incubation</span>
            <ArrowRight size={20} />
            <span className="font-semibold">Published Content</span>
          </div>
        </motion.div>

        {/* Value Proposition */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 1 }}
          viewport={{ once: true }}
          className="text-center mt-16"
        >
          <div className="bg-gradient-to-r from-primary-50 to-green-50 rounded-2xl p-8 max-w-4xl mx-auto">
            <h3 className="text-2xl font-bold mb-4 gradient-text">
              Not Just Another Note App
            </h3>
            <p className="text-lg text-gray-700 mb-6">
              Seethink is designed specifically for the creative process - from the moment inspiration strikes to the final published piece.
            </p>
            <div className="grid md:grid-cols-3 gap-6 text-center">
              <div>
                <div className="text-3xl font-bold text-primary-600 mb-2">Capture</div>
                <div className="text-gray-600">Instant voice recording anywhere</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-primary-600 mb-2">Incubate</div>
                <div className="text-gray-600">Visual growth tracking system</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-primary-600 mb-2">Publish</div>
                <div className="text-gray-600">Direct GitHub integration</div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  )
}

export default ProblemSolution
